import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useChat } from "../../contexts/ChatContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useNavigate, useParams } from "react-router-dom";
import { useIsMobile, useDeviceType } from "../../App";
import Button from "../../components/UI/Button";

// Simple Portal component
const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const ArchiveScreen = () => {
  const {
    chats,
    deleteChat,
    updateChatTitle,
    unarchiveChat,
  } = useChat();
  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();
  const { chatId } = useParams();

  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmUnarchiveId, setConfirmUnarchiveId] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const menuBtnRefs = useRef({});
  const menuRef = useRef(null);

  const archivedChats = chats.filter((chat) => !!chat.is_archived);

  const handleChatSelect = (chatId) => {
    navigate(`/archive/${chatId}`);
  };

  const handleRename = (chat) => {
    setEditingChatId(chat.id);
    setNewTitle(chat.title);
    setMenuOpenId(null);
  };

  const handleRenameSubmit = async (chatId) => {
    if (newTitle.trim()) {
      setIsRenaming(true);
      await updateChatTitle(chatId, newTitle.trim());
      setIsRenaming(false);
    }
    setEditingChatId(null);
    setNewTitle("");
  };

  const openMenu = (chatId, e) => {
    e.stopPropagation();
    const btn = menuBtnRefs.current[chatId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 160;
      
      let left = rect.left + window.scrollX;
      
      if (deviceType === 'tablet' || deviceType === 'mobile') {
        left = rect.right + window.scrollX - menuWidth;
        
        if (left < 10) {
          left = 10;
        }
      } else {
        if (left + menuWidth > window.innerWidth - 10) {
          left = rect.right + window.scrollX - menuWidth;
        }
      }
      
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: left,
      });
    }
    setMenuOpenId(menuOpenId === chatId ? null : chatId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e) => {
      const menuNode = menuRef.current;
      const btnNode = menuBtnRefs.current[menuOpenId];
      if (
        menuNode &&
        !menuNode.contains(e.target) &&
        btnNode &&
        !btnNode.contains(e.target)
      ) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpenId]);

  const containerStyle = {
    maxWidth: deviceType === 'desktop' ? 900 : "100vw",
    margin: deviceType === 'desktop' ? "40px auto" : "0",
    padding: deviceType === 'desktop' ? "32px" : deviceType === 'tablet' ? "24px 16px" : "12px 4px",
    background: theme.surface,
    borderRadius: deviceType === 'desktop' ? 16 : deviceType === 'tablet' ? 12 : 8,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    color: theme.text,
    minHeight: "60vh",
    boxSizing: "border-box",
  };

  const chatListStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "20px",
  };

  const chatItemStyles = (isActive) => ({
    padding: deviceType === 'desktop' ? "16px" : deviceType === 'tablet' ? "14px" : "12px",
    cursor: "pointer",
    backgroundColor: isActive ? theme.primary + "15" : theme.background,
    border: `1px solid ${isActive ? theme.primary : theme.border}`,
    borderRadius: deviceType === 'desktop' ? "8px" : "6px",
    transition: "all 0.2s ease",
    borderLeft: isActive ? `4px solid ${theme.primary}` : `4px solid transparent`,
  });

  const chatTitleStyles = {
    fontSize: deviceType === 'desktop' ? "16px" : deviceType === 'tablet' ? "15px" : "14px",
    fontWeight: "500",
    color: theme.text,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const menuBtnStyles = {
    background: "none",
    border: "none",
    color: theme.isDark ? "#fff" : "#888",
    fontSize: deviceType === 'tablet' ? "22px" : "20px",
    cursor: "pointer",
    padding: deviceType === 'tablet' ? "6px 10px" : "4px 8px",
    borderRadius: "6px",
    transition: "background 0.2s",
    opacity: 0.7,
    minWidth: deviceType === 'tablet' ? "44px" : "auto",
    minHeight: deviceType === 'tablet' ? "44px" : "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const menuStyles = {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "160px",
    background: theme.isDark ? "#23272f" : "#fff",
    color: theme.isDark ? "#fff" : "#23272f",
    border: `1px solid ${theme.isDark ? "#333" : "#e0e0e0"}`,
    borderRadius: "12px",
    boxShadow: theme.isDark
      ? "0 4px 24px 0 rgba(0,0,0,0.7)"
      : "0 4px 24px 0 rgba(0,0,0,0.08)",
    zIndex: 9999,
    padding: "4px 0",
    transition: "all 0.2s",
  };

  const menuItemStyles = {
    width: "100%",
    background: "none",
    border: "none",
    color: "inherit",
    textAlign: "left",
    fontSize: "14px",
    padding: "8px 16px",
    cursor: "pointer",
    borderRadius: "0",
    transition: "background 0.2s",
    outline: "none",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const menuItemDanger = {
    ...menuItemStyles,
    color: "#e53935",
    fontWeight: 500,
  };

  const renameInputStyles = {
    width: "100%",
    fontSize: "15px",
    padding: "6px 10px",
    borderRadius: "8px",
    border: `1px solid ${theme.isDark ? "#444" : "#bbb"}`,
    background: theme.isDark ? "#23272f" : "#fff",
    color: theme.isDark ? "#fff" : "#23272f",
    outline: "none",
    margin: "2px 0",
  };

  // Confirm Delete Modal
  const ConfirmDeleteModal = ({ open, onCancel, onConfirm }) => {
    if (!open) return null;
    return (
      <Portal>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: theme.isDark ? "#23272f" : "#fff",
              color: theme.text,
              borderRadius: deviceType === 'desktop' ? 12 : 8,
              padding: deviceType === 'tablet' ? "28px 20px" : deviceType === 'desktop' ? "32px 24px" : "24px 16px",
              minWidth: deviceType === 'tablet' ? 320 : 280,
              maxWidth: deviceType === 'tablet' ? "80vw" : "90vw",
              boxShadow: "0 8px 32px 0 #0005",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: deviceType === 'tablet' ? 20 : 18,
                fontWeight: 600,
                marginBottom: 20,
                color: theme.text,
              }}
            >
              {t("confirmDeleteChat")}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <Button
                onClick={onCancel}
                size={isMobile ? "small" : "medium"}
                variant="secondary"
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={onConfirm}
                size={isMobile ? "small" : "medium"}
                variant="danger"
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  // Confirm Unarchive Modal
  const ConfirmUnarchiveModal = ({ open, onCancel, onConfirm }) => {
    if (!open) return null;
    return (
      <Portal>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: theme.isDark ? "#23272f" : "#fff",
              color: theme.text,
              borderRadius: deviceType === 'desktop' ? 12 : 8,
              padding: deviceType === 'tablet' ? "28px 20px" : deviceType === 'desktop' ? "32px 24px" : "24px 16px",
              minWidth: deviceType === 'tablet' ? 320 : 280,
              maxWidth: deviceType === 'tablet' ? "80vw" : "90vw",
              boxShadow: "0 8px 32px 0 #0005",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: deviceType === 'tablet' ? 20 : 18,
                fontWeight: 600,
                marginBottom: 12,
                color: theme.text,
              }}
            >
              {t("unarchiveConfirmation")}
            </div>
            <div
              style={{
                fontSize: 16,
                marginBottom: "8%",
                color: theme.textSecondary,
              }}
            >
              {t("unarchiveConfirmationDesc")}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <Button
                onClick={onCancel}
                size={isMobile ? "small" : "medium"}
                variant="secondary"
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={onConfirm}
                size={isMobile ? "small" : "medium"}
                variant="primary"
              >
                {t("unarchiveChat")}
              </Button>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  return (
    <div style={containerStyle}>
      <h1
        style={{
          fontSize: deviceType === 'desktop' ? "32px" : deviceType === 'tablet' ? "28px" : "24px",
          fontWeight: "600",
          color: theme.text,
          marginBottom: "8px",
        }}
      >
        {t("archive")}
      </h1>

      {archivedChats.length === 0 ? (
        <div
          style={{
            color: theme.textSecondary,
            fontSize: 16,
            marginTop: 40,
            textAlign: "center",
          }}
        >
          {t("noArchivedChats")}
        </div>
      ) : (
        <>
          {!chatId && archivedChats.length > 0 && (
            <div
              style={{
                color: theme.textSecondary,
                fontSize: 14,
                marginBottom: "16px",
                textAlign: "center",
              }}
            >
              {t("chooseChatToSeeMessages")}
            </div>
          )}
          <div style={chatListStyles}>
            {archivedChats.map((chat) => {
              const isActive = chatId === chat.id;
              return (
                <div
                  key={chat.id}
                  style={{
                    ...chatItemStyles(isActive),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "relative",
                  }}
                  onClick={() => handleChatSelect(chat.id)}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = theme.isDark
                        ? "#2a2a2a"
                        : "#f0f0f0";
                    }
                    const btn = e.currentTarget.querySelector(".menu-btn");
                    if (btn) btn.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = theme.background;
                    }
                    const btn = e.currentTarget.querySelector(".menu-btn");
                    if (btn && menuOpenId !== chat.id) btn.style.opacity = "0.7";
                  }}
                >
                  {editingChatId === chat.id ? (
                    <input
                      type="text"
                      value={newTitle}
                      autoFocus
                      onChange={(e) => setNewTitle(e.target.value)}
                      onBlur={() => handleRenameSubmit(chat.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenameSubmit(chat.id);
                        if (e.key === "Escape") {
                          setEditingChatId(null);
                          setNewTitle("");
                        }
                      }}
                      disabled={isRenaming}
                      style={renameInputStyles}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div style={chatTitleStyles}>{chat.title}</div>
                      <div style={{ position: "relative" }}>
                        <button
                          className="menu-btn"
                          style={menuBtnStyles}
                          ref={(el) => (menuBtnRefs.current[chat.id] = el)}
                          onClick={(e) => openMenu(chat.id, e)}
                        >
                          ⋯
                        </button>
                        {menuOpenId === chat.id && (
                          <Portal>
                            <div
                              style={{
                                ...menuStyles,
                                position: "absolute",
                                top: menuPosition.top,
                                left: menuPosition.left,
                              }}
                              ref={menuRef}
                            >
                              <button
                                style={menuItemStyles}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRename(chat);
                                }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M16.862 5.487a2.06 2.06 0 0 1 2.915 2.914l-9.193 9.193a2 2 0 0 1-.707.464l-3.11 1.037a.5.5 0 0 1-.634-.634l1.037-3.11a2 2 0 0 1 .464-.707l9.193-9.193Z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {t("rename")}
                              </button>
                              <button
                                style={menuItemStyles}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setConfirmUnarchiveId(chat.id);
                                  setMenuOpenId(null);
                                }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3 7h18M5 7v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {t("unarchiveChat")}
                              </button>
                              <button
                                style={menuItemDanger}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteId(chat.id);
                                  setMenuOpenId(null);
                                }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M6 7h12M10 11v4M14 11v4M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12"
                                    stroke="#e53935"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
                                    stroke="#e53935"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {t("delete")}
                              </button>
                            </div>
                          </Portal>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <ConfirmDeleteModal
        open={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (confirmDeleteId) {
            await deleteChat(confirmDeleteId);
            setConfirmDeleteId(null);
            if (chatId === confirmDeleteId) {
              navigate("/archive");
            }
          }
        }}
      />

      <ConfirmUnarchiveModal
        open={!!confirmUnarchiveId}
        onCancel={() => setConfirmUnarchiveId(null)}
        onConfirm={async () => {
          if (confirmUnarchiveId) {
            await unarchiveChat(confirmUnarchiveId);
            setConfirmUnarchiveId(null);
            if (chatId === confirmUnarchiveId) {
              navigate("/chat");
            }
          }
        }}
      />
    </div>
  );
};

export default ArchiveScreen;
